// Community -> Live Activity -> ACTIVITY HISTORY.
// "Visited today" (one MongoDB document per day), deliberately separate from
// the Live "Active now" numbers above it. Nothing is requested until Family
// opens the section. The switch only controls history RECORDING; Live
// Activity keeps working whatever its state.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaCalendarAlt, FaCity, FaGlobeAmericas, FaMapMarkerAlt } from "react-icons/fa";
import {
  getActivityHistoryAPI, getActivityHistorySettingsAPI, updateActivityHistorySettingsAPI, cleanActivityHistoryAPI,
} from "../../auth/liveApi";
import { formatCount } from "./LiveParts";
import { elakayIntlLocale, elakayPluralText } from "../../pageInside/dataElakay";

const PERIODS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["last_7", "Last 7 Days"], ["last_30", "Last 30 Days"],
  ["this_month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom Date Range"],
];
const RETENTION = [[7, "7 days"], [15, "15 days"], [30, "30 days"], [90, "90 days"], [0, "No automatic deletion"]];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Port-au-Prince", "America/Santo_Domingo", "Europe/Paris", "UTC"];
const COLUMNS = [
  ["anonymousVisitors", "Visitors"], ["users", "Users"], ["royalUsers", "Royal"], ["sellers", "Sellers"], ["providers", "Providers"],
  ["associates", "Associates"], ["investors", "Investors"], ["administrators", "Admins"], ["family", "Family"],
];
const AMERICAS_COUNTRIES = [
  "Antigua and Barbuda", "Argentina", "Bahamas", "Barbados", "Belize", "Bolivia", "Brazil", "Canada", "Chile", "Colombia",
  "Costa Rica", "Cuba", "Dominica", "Dominican Republic", "Ecuador", "El Salvador", "Grenada", "Guatemala", "Guyana", "Haiti",
  "Honduras", "Jamaica", "Mexico", "Nicaragua", "Panama", "Paraguay", "Peru", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Suriname", "Trinidad and Tobago", "United States", "Uruguay", "Venezuela",
];

const formatDay = (day) => {
  const d = new Date(`${day}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString(elakayIntlLocale(), { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
};

const sumRows = (rows) => {
  const sums = Object.fromEntries([...COLUMNS.map(([key]) => key), "otherRoles", "totalAuthenticated", "totalUniquePeople"].map((key) => [key, 0]));
  rows.forEach((row) => Object.keys(sums).forEach((key) => { sums[key] += Number(row[key]) || 0; }));
  return sums;
};

const GeographyControls = ({ geography, filters, setFilters, groupBy, setGroupBy, t }) => {
  const countries = [...new Set([...AMERICAS_COUNTRIES, ...geography.map((r) => r.country).filter((v) => v && v !== "Unknown")])].sort((a, b) => a.localeCompare(b));
  const states = [...new Set(geography.filter((r) => !filters.country || r.country === filters.country).map((r) => r.state))].sort((a, b) => a.localeCompare(b));
  const cities = [...new Set(geography.filter((r) => (!filters.country || r.country === filters.country) && (!filters.state || r.state === filters.state)).map((r) => r.city))].sort((a, b) => a.localeCompare(b));
  const groups = [
    ["date", FaCalendarAlt, t("groupDate", "Date")], ["country", FaGlobeAmericas, t("groupCountry", "Country")],
    ["state", FaMapMarkerAlt, t("groupState", "State / Region")], ["city", FaCity, t("groupCity", "City")],
  ];
  const select = (key, value) => setFilters((current) => ({
    ...current, [key]: value,
    ...(key === "country" ? { state: "", city: "" } : {}), ...(key === "state" ? { city: "" } : {}),
  }));
  return (
    <div className="live-geo-panel" aria-label={t("geoFilters", "Geographic filters")}>
      <div className="live-geo-heading">
        <div><strong>{t("exploreLocation", "Explore activity by location")}</strong><span>{t("exploreLocationHint", "Filter the selected period, then choose how results are grouped.")}</span></div>
        {(filters.country || filters.state || filters.city) && <button type="button" className="live-geo-reset" onClick={() => setFilters({ country: "", state: "", city: "" })}>{t("clearFilters", "Clear filters")}</button>}
      </div>
      <div className="live-geo-controls">
        <label><span>{t("groupCountry", "Country")}</span><select aria-label={t("groupCountry", "Country")} value={filters.country} onChange={(e) => select("country", e.target.value)}><option value="">{t("allCountries", "All countries")}</option>{countries.map((v) => <option key={v}>{v}</option>)}</select></label>
        <label><span>{t("groupState", "State / Region")}</span><select aria-label={t("groupState", "State / Region")} value={filters.state} disabled={!states.length} onChange={(e) => select("state", e.target.value)}><option value="">{t("allStates", "All states / regions")}</option>{states.map((v) => <option key={v}>{v}</option>)}</select></label>
        <label><span>{t("groupCity", "City")}</span><select aria-label={t("groupCity", "City")} value={filters.city} disabled={!cities.length} onChange={(e) => select("city", e.target.value)}><option value="">{t("allCities", "All cities")}</option>{cities.map((v) => <option key={v}>{v}</option>)}</select></label>
      </div>
      <div className="live-group-switch" role="group" aria-label={t("groupResults", "Group results by")}>
        <span>{t("groupResults", "Group by")}</span>
        <div>{groups.map(([key, Icon, label]) => <button type="button" key={key} className={groupBy === key ? "is-active" : ""} aria-pressed={groupBy === key} onClick={() => setGroupBy(key)}><Icon aria-hidden="true" />{label}</button>)}</div>
      </div>
    </div>
  );
};

const Switch = ({ checked, onChange, disabled, label, onText = "ON", offText = "OFF" }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} className={`live-switch ${checked ? "is-on" : ""}`} disabled={disabled} onClick={() => onChange(!checked)}>
    <span className="live-switch-track"><span className="live-switch-thumb" /></span>
    <span className="live-switch-text">{checked ? onText : offText}</span>
  </button>
);

const CleanModal = ({ show, onHide, onConfirm, retentionDays, busy, t }) => {
  const [mode, setMode] = useState("expired");
  const [includeToday, setIncludeToday] = useState(false);
  useEffect(() => { if (show) { setMode("expired"); setIncludeToday(false); } }, [show]);
  return (
    <Modal show={show} onHide={onHide} centered className="live-modal" aria-labelledby="live-clean-title">
      <Modal.Header closeButton><Modal.Title as="h2" id="live-clean-title" className="live-modal-title">{t("cleanTitle", "Clean activity history")}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{t("cleanWarn", "Are you sure you want to clean activity history? This action will permanently delete the selected daily activity history records.")}</p>
        <fieldset className="live-choices">
          <legend className="live-sr">{t("cleanWhat", "What to delete")}</legend>
          <label className="live-choice">
            <input type="radio" name="live-clean-mode" checked={mode === "expired"} onChange={() => setMode("expired")} />
            <span><strong>{t("cleanExpired", "Delete expired history")}</strong><small>{retentionDays > 0 ? t("olderThanRetention", "Days older than the {value0}-day retention", { value0: retentionDays }) : t("noRetention", "Retention is “No automatic deletion”: nothing is expired.")}</small></span>
          </label>
          <label className="live-choice">
            <input type="radio" name="live-clean-mode" checked={mode === "all"} onChange={() => setMode("all")} />
            <span><strong>{t("cleanAll", "Delete all history")}</strong><small>{t("cleanAllHint", "Every saved day. Today is kept unless you include it below.")}</small></span>
          </label>
          {mode === "all" && (
            <label className="live-choice live-choice-sub">
              <input type="checkbox" checked={includeToday} onChange={(e) => setIncludeToday(e.target.checked)} />
              <span><strong>{t("includeToday", "Also delete today")}</strong><small>{t("includeTodayHint", "Today's counters restart from zero.")}</small></span>
            </label>
          )}
        </fieldset>
        <p className="live-muted">{t("cleanLive", "Live Activity is not affected.")}</p>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="live-btn live-btn-ghost" onClick={onHide}>{t("cancel", "Cancel")}</button>
        <button type="button" className="live-btn live-btn-danger" disabled={busy} onClick={() => onConfirm({ mode, includeToday: mode === "all" && includeToday })}>
          {busy ? t("deleting", "Deleting…") : t("confirmDelete", "Delete permanently")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const ActivityHistoryPanel = ({ t }) => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [period, setPeriod] = useState("last_7");
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState({ loading: false, error: null, result: null });
  const [message, setMessage] = useState("");
  const [cleanOpen, setCleanOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [groupBy, setGroupBy] = useState("date");
  const [geoFilters, setGeoFilters] = useState({ country: "", state: "", city: "" });

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    getActivityHistorySettingsAPI({ signal: controller.signal })
      .then((r) => setSettings(r.settings))
      .catch((e) => { if (e.name !== "AbortError") setMessage(e.message); });
    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (period === "custom" && (!range.from || !range.to)) return undefined;
    const controller = new AbortController();
    setData((d) => ({ ...d, loading: true, error: null }));
    getActivityHistoryAPI({ period, ...(period === "custom" ? range : {}) }, { signal: controller.signal })
      .then((result) => setData({ loading: false, error: null, result }))
      .catch((e) => { if (e.name !== "AbortError") setData({ loading: false, error: e.message, result: null }); });
    return () => controller.abort();
  }, [open, period, range, reloadKey]);

  const saveSettings = useCallback(async (patch) => {
    setSavingSettings(true);
    setMessage("");
    try {
      const r = await updateActivityHistorySettingsAPI(patch);
      setSettings(r.settings);
      setMessage(t("saved", "Saved."));
    } catch (e) {
      setMessage(e.message);
    } finally {
      setSavingSettings(false);
    }
  }, [t]);

  const clean = async (body) => {
    setCleaning(true);
    try {
      const r = await cleanActivityHistoryAPI(body);
      setMessage(elakayPluralText(r.deleted, ["h_live_days_deleted_one", "{value0} day deleted."], ["h_live_days_deleted", "{value0} days deleted."], { value0: formatCount(r.deleted) }));
      setCleanOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setMessage(e.message);
    } finally {
      setCleaning(false);
    }
  };

  const result = data.result;
  const totals = result?.totals;
  const geography = result?.geography || [];
  const groupedGeography = useMemo(() => {
    const filtered = geography.filter((row) => (!geoFilters.country || row.country === geoFilters.country)
      && (!geoFilters.state || row.state === geoFilters.state) && (!geoFilters.city || row.city === geoFilters.city));
    if (groupBy === "date") return [];
    const groups = new Map();
    filtered.forEach((row) => {
      const key = groupBy === "country" ? row.country : groupBy === "state" ? `${row.country}\u001f${row.state}` : `${row.country}\u001f${row.state}\u001f${row.city}`;
      const list = groups.get(key) || [];
      list.push(row); groups.set(key, list);
    });
    return [...groups.entries()].map(([key, rows]) => ({ key, parts: key.split("\u001f"), ...sumRows(rows) })).sort((a, b) => b.totalUniquePeople - a.totalUniquePeople);
  }, [geography, geoFilters, groupBy]);
  const visibleDailyRows = (result?.rows || []).map((row) => {
    if (!geoFilters.country && !geoFilters.state && !geoFilters.city) return row;
    return { ...row, ...sumRows(geography.filter((g) => g.date === row.date && (!geoFilters.country || g.country === geoFilters.country) && (!geoFilters.state || g.state === geoFilters.state) && (!geoFilters.city || g.city === geoFilters.city))) };
  }).filter((row) => row.totalUniquePeople > 0);
  const displayRows = groupBy === "date" ? visibleDailyRows : groupedGeography;
  const displayHasOther = displayRows.some((r) => r.otherRoles > 0);
  const hasGeoFilter = Boolean(geoFilters.country || geoFilters.state || geoFilters.city || groupBy !== "date");
  const viewTotals = hasGeoFilter ? sumRows(displayRows) : totals;

  return (
    <section className="live-card" aria-labelledby="live-history-title">
      <header className="live-card-head">
        <h3 id="live-history-title">{t("historyTitle", "ACTIVITY HISTORY")}</h3>
        <span className="live-muted">{t("historyHint", "People who visited each day — not the same as “active now”.")}</span>
        <button type="button" className="live-btn live-btn-ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? t("hide", "Hide") : t("showHistory", "Show Activity History")}
        </button>
      </header>

      {open && (
        <>
          <div className="live-history-settings">
            <div className="live-setting">
              <span className="live-setting-label">{t("enableHistory", "Enable Activity History")}</span>
              <Switch checked={Boolean(settings?.enabled)} disabled={!settings || savingSettings} label={t("enableHistory", "Enable Activity History")} onText={t("switchOn", "ON")} offText={t("switchOff", "OFF")} onChange={(enabled) => saveSettings({ enabled })} />
              <small className="live-muted">{settings?.enabled ? t("historyOn", "Each person is saved once per day in the day's document.") : t("historyOff", "Nothing new is saved. Live Activity keeps working.")}</small>
            </div>
            <label className="live-setting" htmlFor="live-retention">
              <span className="live-setting-label">{t("retention", "History Retention")}</span>
              <select id="live-retention" value={settings ? String(settings.retentionDays) : ""} disabled={!settings || savingSettings} onChange={(e) => saveSettings({ retentionDays: Number(e.target.value) })}>
                {RETENTION.map(([v, label]) => <option key={v} value={String(v)}>{t(`retention_${v}`, label)}</option>)}
              </select>
              <small className="live-muted">{t("retentionHint", "Deleted automatically every hour; today is always kept.")}</small>
            </label>
            <label className="live-setting" htmlFor="live-timezone">
              <span className="live-setting-label">{t("timezone", "Day boundaries (time zone)")}</span>
              <select id="live-timezone" value={settings?.timezone || ""} disabled={!settings || savingSettings} onChange={(e) => saveSettings({ timezone: e.target.value })}>
                {[...new Set([...(settings?.timezone ? [settings.timezone] : []), ...TIMEZONES])].map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
              <small className="live-muted">{t("timezoneHint", "A day runs 00:00:00 → 23:59:59.999 in this zone.")}</small>
            </label>
            <div className="live-setting">
              <span className="live-setting-label">{t("cleanHistory", "Clean History")}</span>
              <button type="button" className="live-btn live-btn-danger-ghost" onClick={() => setCleanOpen(true)}>{t("cleanHistory", "Clean History")}</button>
            </div>
          </div>
          {message && <p className="live-muted" role="status">{message}</p>}

          <div className="live-filterbar live-filterbar-inline">
            <label htmlFor="live-history-period"><span>{t("period", "Period")}</span>
              <select id="live-history-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
                {PERIODS.map(([v, label]) => <option key={v} value={v}>{t(`p_${v}`, label)}</option>)}
              </select>
            </label>
            {period === "custom" && (
              <>
                <label htmlFor="live-history-from"><span>{t("from", "From")}</span><input id="live-history-from" type="date" value={range.from} max={range.to || undefined} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} /></label>
                <label htmlFor="live-history-to"><span>{t("to", "To")}</span><input id="live-history-to" type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} /></label>
              </>
            )}
            {result && <span className="live-muted">{formatDay(result.range.from)} → {formatDay(result.range.to)} · {result.settings.timezone}</span>}
          </div>

          {data.error && <p className="live-error" role="alert">{data.error} <button type="button" className="live-link" onClick={() => setReloadKey((k) => k + 1)}>{t("retry", "Retry")}</button></p>}

          {totals && (
            <div className="live-tiles">
              <div className="live-tile"><span>{result.days > 1 ? t("dailyVisits", "Total daily unique visits") : t("uniquePeople", "Unique people")}</span><strong>{formatCount(viewTotals.totalUniquePeople)}</strong>{result.days > 1 && <small>{t("dailyVisitsHint", "Sum of each day's unique people — someone seen on 3 days counts 3 times.")}</small>}</div>
              <div className="live-tile"><span>{t("authenticatedShort", "Authenticated")}</span><strong>{formatCount(viewTotals.totalAuthenticated)}</strong></div>
              <div className="live-tile"><span>{t("anonymous", "Anonymous visitors")}</span><strong>{formatCount(viewTotals.anonymousVisitors)}</strong></div>
              <div className="live-tile"><span>{groupBy === "date" ? t("daysWithData", "Days with data") : t("groupsShown", "Groups shown")}</span><strong>{formatCount(groupBy === "date" ? visibleDailyRows.length : groupedGeography.length)}</strong></div>
            </div>
          )}

          <GeographyControls geography={geography} filters={geoFilters} setFilters={setGeoFilters} groupBy={groupBy} setGroupBy={setGroupBy} t={t} />

          <div className="live-table-wrap">
            <table className="live-table live-history-table">
              <thead>
                <tr>
                  <th>{groupBy === "date" ? t("date", "Date") : groupBy === "country" ? t("groupCountry", "Country") : groupBy === "state" ? t("groupState", "State / Region") : t("groupCity", "City")}</th>
                  {COLUMNS.map(([k, label]) => <th key={k} className="num">{t(`c_${k}`, label)}</th>)}
                  {displayHasOther && <th className="num">{t("other", "Other")}</th>}
                  <th className="num">{t("totalCol", "Total")}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r) => (
                  <tr key={r.date || r.key}>
                    <td data-label={groupBy === "date" ? t("date", "Date") : t("location", "Location")}>
                      {groupBy === "date" ? formatDay(r.date) : <span className="live-location-cell"><strong>{r.parts[r.parts.length - 1]}</strong>{r.parts.length > 1 && <small>{r.parts.slice(0, -1).reverse().join(" · ")}</small>}</span>}
                    </td>
                    {COLUMNS.map(([k, label]) => <td key={k} className="num" data-label={t(`c_${k}`, label)}>{formatCount(r[k])}</td>)}
                    {displayHasOther && <td className="num" data-label={t("other", "Other")}>{formatCount(r.otherRoles)}</td>}
                    <td className="num" data-label={t("totalCol", "Total")}><strong>{formatCount(r.totalUniquePeople)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.loading && !result && <p className="live-muted">{t("loading", "Loading…")}</p>}
            {result && !result.rows.length && (
              <p className="live-muted">{settings?.enabled ? t("noHistory", "No saved day for this period yet.") : t("noHistoryOff", "No saved day for this period. Turn on Activity History to start saving.")}</p>
            )}
            {result && result.rows.length > 0 && !displayRows.length && <div className="live-geo-empty"><FaMapMarkerAlt aria-hidden="true" /><strong>{t("noGeoResults", "No activity matches these location filters.")}</strong><button type="button" className="live-link" onClick={() => setGeoFilters({ country: "", state: "", city: "" })}>{t("clearFilters", "Clear filters")}</button></div>}
          </div>
        </>
      )}

      <CleanModal show={cleanOpen} onHide={() => setCleanOpen(false)} onConfirm={clean} retentionDays={settings?.retentionDays || 0} busy={cleaning} t={t} />
    </section>
  );
};

export default ActivityHistoryPanel;
